import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { components } from "../../types/api";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import { useCreateUserMutation } from "../../store/apis/usersApi";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import PageContainer from "../../components/layout/PageContainer";
import { useGoBack } from "../../hooks/useGoBack";

type Role = components["schemas"]["_36_Enums.Role"];

const NewUser: React.FC = () => {
  const collapsed = useSelector(selectSidebarCollapsed);
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useGoBack();

  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "CLIENT" as Role,
    active: true,
  });

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "password") {
      setPassword(value);
    } else {
      setUserData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setUserData((prev) => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserData((prev) => ({
      ...prev,
      active: e.target.checked,
    }));
  };

  const [
    createUser,
    { isLoading, data: userCreateData, error: userCreateError },
  ] = useCreateUserMutation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (userCreateData) {
      dispatch(
        setToastMessage({
          message: t("users.notifications.createSuccess"),
          severity: "success",
        })
      );
      navigate("/users");
    }
  }, [userCreateData, dispatch, navigate, t]);

  useEffect(() => {
    if (userCreateError && (userCreateError as FetchBaseQueryError)) {
      console.error("Error creating user:", userCreateError);
      dispatch(
        setToastMessage({
          message:
            userCreateError &&
            "data" in userCreateError &&
            typeof userCreateError.data === "object" &&
            userCreateError.data &&
            "message" in userCreateError.data
              ? String(userCreateError.data.message)
              : t("users.notifications.createError"),
          severity: "error",
        })
      );
    }
  }, [userCreateError, dispatch, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    createUser({
      password,
      user: userData,
    });
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          px: 4,
          py: 2,

          ...(collapsed && {
            ml: `calc(${theme.spacing(7)} + 1px)`,
          }),
          ...(!collapsed && {
            ml: `calc(${theme.spacing(32)} + 1px)`,
          }),
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: 700,
                  color: theme.palette.grey[900],
                }}
              >
                {t("users.newUser")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Nunito",
                  color: theme.palette.grey[600],
                }}
              >
                {t("users.createNewUser")}
              </Typography>
            </Box>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: "flex", flexDirection: "column", gap: 4 }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 4,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("users.firstName")}
                    </Typography>
                    <InfoIcon
                      sx={{ fontSize: 16, color: theme.palette.grey[600] }}
                    />
                  </Box>
                  <TextField
                    required
                    name="firstName"
                    placeholder={t("users.firstName")}
                    value={userData.firstName}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: theme.palette.grey[200],
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("users.lastName")}
                  </Typography>
                  <TextField
                    required
                    name="lastName"
                    placeholder={t("users.lastName")}
                    value={userData.lastName}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: theme.palette.grey[200],
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("users.active")}
                    </Typography>
                    <InfoIcon
                      sx={{ fontSize: 16, color: theme.palette.grey[600] }}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={userData.active}
                        onChange={handleSwitchChange}
                        name="active"
                      />
                    }
                    label={
                      userData.active
                        ? t("userStatus.active")
                        : t("userStatus.inactive")
                    }
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontFamily: "Nunito",
                        color: theme.palette.grey[600],
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 4,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("users.email")}
                  </Typography>
                  <TextField
                    required
                    name="email"
                    type="email"
                    placeholder={t("users.email")}
                    value={userData.email}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: theme.palette.grey[200],
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("users.password")}
                  </Typography>
                  <TextField
                    required
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("users.password")}
                    value={password}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: theme.palette.grey[400] }}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: theme.palette.grey[200],
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("users.phone")}
                  </Typography>
                  <TextField
                    name="phone"
                    placeholder={t("users.phone")}
                    value={userData.phone}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: theme.palette.grey[200],
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("users.role")}
                    </Typography>
                    <InfoIcon
                      sx={{ fontSize: 16, color: theme.palette.grey[600] }}
                    />
                  </Box>
                  <FormControl fullWidth>
                    <Select
                      name="role"
                      value={userData.role}
                      onChange={handleSelectChange}
                      displayEmpty
                      sx={{
                        borderRadius: 50,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.grey[200],
                        },
                      }}
                    >
                      <MenuItem value="ADMIN">
                        {t("users.roles.admin")}
                      </MenuItem>
                      <MenuItem value="ACCOUNT">
                        {t("users.roles.account")}
                      </MenuItem>
                      <MenuItem value="CLIENT">
                        {t("users.roles.client")}
                      </MenuItem>
                      <MenuItem value="TECHNICIAN">
                        {t("users.roles.technician", {
                          defaultValue: "Technician",
                        })}
                      </MenuItem>
                      <MenuItem value="PRICING">
                        {t("users.roles.pricing", { defaultValue: "Pricing" })}
                      </MenuItem>
                      <MenuItem value="COMERCIAL">
                        {t("users.roles.comercial", {
                          defaultValue: "Comercial",
                        })}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
              >
                <Button
                  variant="outlined"
                  onClick={() => goBack("/users")}
                  startIcon={<KeyboardBackspaceIcon />}
                  sx={{
                    borderRadius: 50,
                    padding: "12px 24px",
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    fontSize: 18,
                    textTransform: "none",
                    boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.04)",
                  }}
                >
                  {t("common.back")}
                </Button>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Button
                    variant="outlined"
                    onClick={() => goBack("/users")}
                    sx={{
                      borderRadius: 50,
                      padding: "12px 24px",
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      fontSize: 18,
                      textTransform: "none",
                      boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.04)",
                    }}
                  >
                    {t("common.discard")}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    sx={{
                      borderRadius: 50,
                      padding: "12px 24px",
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      fontSize: 18,
                      textTransform: "none",
                      boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.04)",
                      bgcolor: theme.palette.grey[900],
                      color: theme.palette.getContrastText(
                        theme.palette.grey[900]
                      ),
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      t("users.confirmNewUser")
                    )}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </PageContainer>
      </Box>
    </Box>
  );
};

export default NewUser;
